import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/feature/AdminLayout';
import {
  fetchContactMessages,
  markAllContactMessagesRead,
  openContactMessage,
  type ContactMessage,
} from '../../api/contactMessages.api';

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function MessagesPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [selected, setSelected] = useState<ContactMessage | null>(null);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const r = await fetchContactMessages({
      per_page: 50,
      search: search.trim() || undefined,
      unread: filter === 'unread' ? true : filter === 'read' ? false : undefined,
    });
    setLoading(false);
    if (!r.ok) {
      setError(r.message || 'تعذر تحميل الرسائل');
      return;
    }
    setItems(r.data);
  }, [filter, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const unreadCount = items.filter((m) => !m.is_read).length;

  const onOpen = async (msg: ContactMessage) => {
    setOpening(true);
    const r = await openContactMessage(msg.id);
    setOpening(false);
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setSelected(r.data);
    setItems((prev) => prev.map((m) => (m.id === msg.id ? { ...m, is_read: true, read_at: r.data.read_at } : m)));
    window.dispatchEvent(new CustomEvent('contact-messages:changed'));
  };

  const onMarkAll = async () => {
    const r = await markAllContactMessagesRead();
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setItems((prev) => prev.map((m) => ({ ...m, is_read: true, read_at: m.read_at ?? new Date().toISOString() })));
    if (selected) setSelected({ ...selected, is_read: true });
    window.dispatchEvent(new CustomEvent('contact-messages:changed'));
  };

  return (
    <AdminLayout>
      <div className="flex flex-col gap-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-black text-gray-900">{t('page.messages.title')}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{t('page.messages.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 || filter !== 'all' ? (
              <button
                type="button"
                onClick={() => void onMarkAll()}
                className="px-3 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold hover:bg-gray-800"
              >
                {t('messages.mark_all_read')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => void load()}
              className="px-3 py-2 rounded-xl bg-white ring-1 ring-gray-200 text-xs font-bold text-gray-700"
            >
              {t('messages.refresh')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100">
            <p className="text-xs text-gray-400 font-medium">{t('messages.summary.total')}</p>
            <p className="text-2xl font-black text-gray-900 mt-1">{loading ? '…' : items.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100">
            <p className="text-xs text-gray-400 font-medium">{t('messages.summary.unread')}</p>
            <p className="text-2xl font-black text-rose-600 mt-1">{loading ? '…' : unreadCount}</p>
          </div>
          <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100 col-span-2 md:col-span-1">
            <p className="text-xs text-gray-400 font-medium">{t('messages.summary.source')}</p>
            <p className="text-sm font-bold text-gray-900 mt-2">{t('messages.summary.source_value')}</p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 rounded-2xl bg-white ring-1 ring-gray-100 overflow-hidden">
            <div className="p-3 border-b border-gray-100 flex flex-col sm:flex-row gap-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void load();
                }}
                placeholder={t('messages.search_placeholder')}
                className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm"
              />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as typeof filter)}
                className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold bg-white"
              >
                <option value="all">{t('messages.filter.all')}</option>
                <option value="unread">{t('messages.filter.unread')}</option>
                <option value="read">{t('messages.filter.read')}</option>
              </select>
              <button
                type="button"
                onClick={() => void load()}
                className="px-3 py-2 rounded-xl bg-teal-600 text-white text-xs font-bold"
              >
                {t('messages.search')}
              </button>
            </div>

            {error ? (
              <div className="m-3 rounded-xl bg-rose-50 text-rose-700 text-sm px-3 py-2">{error}</div>
            ) : null}

            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[720px]">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    {[
                      t('messages.col.status'),
                      t('messages.col.name'),
                      t('messages.col.email'),
                      t('messages.col.phone'),
                      t('messages.col.atelier'),
                      t('messages.col.message'),
                      t('messages.col.date'),
                    ].map((h) => (
                      <th key={h} className="text-right font-bold px-3 py-3 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-10 text-center text-gray-400">
                        {t('messages.loading')}
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-10 text-center text-gray-400">
                        {t('messages.empty')}
                      </td>
                    </tr>
                  ) : (
                    items.map((m) => (
                      <tr
                        key={m.id}
                        onClick={() => void onOpen(m)}
                        className={`border-t border-gray-50 cursor-pointer hover:bg-teal-50/40 ${
                          !m.is_read ? 'bg-rose-50/40' : ''
                        }`}
                      >
                        <td className="px-3 py-3">
                          {!m.is_read ? (
                            <span className="inline-flex items-center gap-1.5 text-rose-600 font-bold">
                              <span className="w-2 h-2 rounded-full bg-rose-500" />
                              {t('messages.unread')}
                            </span>
                          ) : (
                            <span className="text-gray-400 font-bold">{t('messages.read')}</span>
                          )}
                        </td>
                        <td className="px-3 py-3 font-bold text-gray-900">{m.name}</td>
                        <td className="px-3 py-3" dir="ltr">
                          {m.email ?? '—'}
                        </td>
                        <td className="px-3 py-3" dir="ltr">
                          {m.phone ?? '—'}
                        </td>
                        <td className="px-3 py-3">{m.atelier_name ?? '—'}</td>
                        <td className="px-3 py-3 max-w-[220px] truncate text-slate-600" title={m.message ?? ''}>
                          {m.message ?? '—'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-gray-500">{formatDate(m.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="w-full lg:w-[360px] rounded-2xl bg-white ring-1 ring-gray-100 p-5 min-h-[280px]">
            {!selected ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-gray-400 py-16">
                <i className="ri-mail-open-line text-3xl mb-3" />
                <p className="text-sm font-bold">{t('messages.detail_hint')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="text-base font-black text-gray-900">{selected.name}</h2>
                    <p className="text-xs text-gray-500 mt-1">{formatDate(selected.created_at)}</p>
                  </div>
                  {!selected.is_read ? (
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-rose-100 text-rose-700">
                      {t('messages.unread')}
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">
                      {t('messages.read')}
                    </span>
                  )}
                </div>
                <dl className="space-y-2 text-sm">
                  {[
                    [t('messages.col.email'), selected.email ?? '—'],
                    [t('messages.col.phone'), selected.phone ?? '—'],
                    [t('messages.col.atelier'), selected.atelier_name ?? '—'],
                  ].map(([k, v]) => (
                    <div key={String(k)} className="flex justify-between gap-3 border-b border-gray-50 pb-2">
                      <dt className="text-xs text-gray-500 font-bold">{k}</dt>
                      <dd className="text-xs font-bold text-gray-900" dir="auto">
                        {v}
                      </dd>
                    </div>
                  ))}
                </dl>
                <div>
                  <p className="text-xs text-gray-500 font-bold mb-2">{t('messages.col.message')}</p>
                  <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {opening ? '…' : selected.message || '—'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
